import type { ReactNode } from 'react';

/**
 * Real toggle rather than a bare checkbox. Still a native `input` underneath, so
 * it keeps keyboard focus, the space bar and screen-reader semantics for free.
 */
export function Switch({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="switch-row">
      <input
        type="checkbox"
        className="switch-input"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="switch-track" aria-hidden="true">
        <span className="switch-thumb" />
      </span>
      <span className="switch-text">
        <span className="switch-label">{label}</span>
        {hint ? <span className="switch-hint">{hint}</span> : null}
      </span>
    </label>
  );
}

/** Native select with a custom chevron; the arrow is drawn, not a font glyph. */
export function Select({
  value,
  onChange,
  children,
  disabled,
  compact,
  ariaLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  children: ReactNode;
  disabled?: boolean;
  compact?: boolean;
  ariaLabel?: string;
}) {
  return (
    <span className={compact ? 'select-wrap compact' : 'select-wrap'}>
      <select
        className={compact ? 'input select compact' : 'input select'}
        value={value}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => onChange(e.target.value)}
      >
        {children}
      </select>
      <svg
        className="select-chevron"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </span>
  );
}

/**
 * Shaped placeholders while data loads. Matching the real layout's geometry
 * stops the page jumping when content arrives, which a spinner cannot do.
 */
export function Skeleton({ w, h = 14, radius = 6 }: { w?: string; h?: number; radius?: number }) {
  return (
    <span
      className="skeleton"
      style={{ width: w ?? '100%', height: h, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="skeleton-list" role="status" aria-label="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skeleton-row">
          <Skeleton w="56px" h={38} radius={5} />
          <div className="skeleton-lines">
            <Skeleton w={`${52 + ((i * 13) % 30)}%`} h={13} />
            <Skeleton w={`${28 + ((i * 7) % 18)}%`} h={11} />
          </div>
          <Skeleton w="52px" h={20} radius={999} />
        </div>
      ))}
    </div>
  );
}

export function GridSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <ul className="card-grid" role="status" aria-label="Loading">
      {Array.from({ length: cards }, (_, i) => (
        <li key={i} className="grid-card">
          <span className="grid-card-link">
            <Skeleton h={116} radius={0} />
            <span className="grid-card-body">
              <Skeleton w={`${45 + ((i * 11) % 25)}%`} h={15} />
              <div style={{ height: 8 }} />
              <Skeleton w="60%" h={11} />
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function FormSkeleton() {
  return (
    <div className="card" role="status" aria-label="Loading">
      <Skeleton w="110px" h={11} />
      <div style={{ height: 20 }} />
      <Skeleton w="30%" h={13} />
      <div style={{ height: 8 }} />
      <Skeleton h={38} radius={7} />
      <div style={{ height: 22 }} />
      <Skeleton w="22%" h={13} />
      <div style={{ height: 8 }} />
      <Skeleton h={96} radius={7} />
    </div>
  );
}

/** Empty state with room for an explanation and the action that fixes it. */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <svg
        className="empty-icon"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M3 7.5 12 3l9 4.5M3 7.5v9L12 21m-9-13.5L12 12m9-4.5v9L12 21m9-13.5L12 12m0 9V12" />
      </svg>
      <p className="empty-title">{title}</p>
      {body ? <p className="empty-body">{body}</p> : null}
      {action ? <div className="empty-action">{action}</div> : null}
    </div>
  );
}

/** Section heading with optional supporting copy, used at the top of each card. */
export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-header">
      <div className="section-header-text">
        <h3>{title}</h3>
        {description ? <p className="section-description">{description}</p> : null}
      </div>
      {action ? <div className="section-header-action">{action}</div> : null}
    </div>
  );
}
